import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import GlobalModals from "components/GlobalModals";
import NotFound from "pages/NotFound";
import Landing from './pages/landing';
import AnalyticsDashboard from './pages/analytics-dashboard';
import Login from './pages/login';
import Register from './pages/register';
import Conversations from './pages/conversations';
import CampaignAutomation from './pages/campaign-automation';
import ClientManagement from './pages/client-management';
import EvergreenTouchpoints from './pages/evergreen-touchpoints';
import Settings from './pages/settings';
import Subscribe from './pages/subscribe';
import StripeVerification from './pages/stripe-verification';
import PolicyPage from './pages/PolicyPage';
import GlobalPolicyPage from './pages/GlobalPolicyPage';
import AgentContactPage from './pages/agent-contact';
import AdminMasquerade from './pages/admin-masquerade';
import { useAuth } from './contexts/AuthContext';

/**
 * Guards app routes that require an active Stripe subscription.
 * Authenticated users without an active/trialing subscription are redirected to /subscribe.
 * Incomplete onboarding → /register.
 * Unauthenticated users are redirected to /login.
 */
function SubscriptionGuard({ children }) {
  const { user, userProfile, loading, profileReady } = useAuth();

  if (loading || (user && !profileReady)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  // Only enforce when column exists (null = incomplete; undefined = pre-migration)
  if (userProfile && userProfile.onboarding_completed_at === null) {
    return <Navigate to="/register" replace />;
  }

  const status = userProfile?.subscription_status;
  if (status !== 'active' && status !== 'trialing') {
    return <Navigate to="/subscribe" replace />;
  }

  return children;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <GlobalModals />
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/privacy-policy" element={<GlobalPolicyPage />} />
        <Route path="/terms" element={<GlobalPolicyPage />} />
        <Route path="/p/:agentId" element={<AgentContactPage />} />
        <Route path="/p/:agentId/privacy" element={<PolicyPage />} />
        <Route path="/p/:agentId/terms" element={<PolicyPage />} />
        <Route path="/stripe-verification" element={<StripeVerification />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/subscribe" element={<Subscribe />} />

        {/* Subscription-gated app routes */}
        <Route path="/analytics-dashboard" element={<SubscriptionGuard><AnalyticsDashboard /></SubscriptionGuard>} />
        <Route path="/conversations" element={<SubscriptionGuard><Conversations /></SubscriptionGuard>} />
        <Route path="/campaign-automation" element={<SubscriptionGuard><CampaignAutomation /></SubscriptionGuard>} />
        <Route path="/client-management" element={<SubscriptionGuard><ClientManagement /></SubscriptionGuard>} />
        <Route path="/evergreen-touchpoints" element={<SubscriptionGuard><EvergreenTouchpoints /></SubscriptionGuard>} />

        {/* Settings is always accessible (needed to subscribe / manage billing) */}
        <Route path="/settings" element={<Settings />} />

        {/* Staff masquerade. Auth is enforced in the page. Kept outside SubscriptionGuard. */}
        <Route path="/admin/masquerade" element={<AdminMasquerade />} />

        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
